/**
 * Configuration SEO centralisée (audit L-6 / A3).
 * Titres, descriptions et helpers pour PageMeta (React 19 metadata hoist).
 */
import { getImageUrl } from '../utils/apiHelpers';
import { readEnv } from './env';

export const SITE = {
  name: 'Kilimmanjaro',
  defaultTitle: 'Kilimmanjaro — Plateforme e-commerce au Togo',
  defaultDescription:
    'Achetez en ligne au Togo : mode, beauté, électronique, maison et plus. Livraison rapide et paiement Mobile Money.',
  defaultOgImagePath: '/logo512.png',
  locale: 'fr_TG',
};

/** Origine publique du site (canonical, og:url). */
export function getSiteOrigin() {
  const fromEnv = readEnv('REACT_APP_SITE_URL')?.trim();
  if (fromEnv) {
    return fromEnv.replace(/\/$/, '');
  }
  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin;
  }
  return 'https://kilimmanjaro.com';
}

/** Titre onglet : « Page | Kilimmanjaro » */
export function formatPageTitle(pageTitle) {
  const trimmed = String(pageTitle || '').trim();
  if (!trimmed) return SITE.defaultTitle;
  if (trimmed.includes(SITE.name)) return trimmed;
  return `${trimmed} | ${SITE.name}`;
}

/** URL absolue pour og:image et canonical. */
export function resolveAbsoluteUrl(pathOrUrl) {
  if (!pathOrUrl) return resolveAbsoluteUrl(SITE.defaultOgImagePath);
  const value = String(pathOrUrl).trim();
  if (/^https?:\/\//i.test(value)) return value;
  const path = value.startsWith('/') ? value : `/${value}`;
  return `${getSiteOrigin()}${path}`;
}

/** Texte brut tronqué pour meta description (~160 car.). */
export function truncateMetaDescription(text, maxLength = 160) {
  if (!text) return '';
  const plain = String(text)
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (plain.length <= maxLength) return plain;
  return `${plain.slice(0, maxLength - 1).trim()}…`;
}

/** Métadonnées statiques des routes publiques principales. */
export const STATIC_PAGES = {
  home: {
    title: 'Accueil',
    description: SITE.defaultDescription,
    canonicalPath: '/',
  },
  categories: {
    title: 'Catégories & produits',
    description:
      'Parcourez les catégories et trouvez vos produits préférés sur Kilimmanjaro.',
    canonicalPath: '/categories',
  },
  privacy: {
    title: 'Politique de confidentialité',
    description:
      'Politique de confidentialité et protection des données personnelles sur Kilimmanjaro.',
    canonicalPath: '/privacy-policy',
  },
  terms: {
    title: 'Conditions générales de vente',
    description: 'Conditions générales de vente de la marketplace Kilimmanjaro.',
    canonicalPath: '/terms-of-sale',
  },
  returns: {
    title: 'Politique de retour',
    description:
      'Conditions de retour et remboursement des commandes Kilimmanjaro.',
    canonicalPath: '/return-policy',
  },
  boutiques: {
    title: 'Boutiques',
    description: 'Découvrez les vendeurs et boutiques partenaires Kilimmanjaro.',
    canonicalPath: '/boutiques',
  },
};

/**
 * SEO fiche produit (données API chargées).
 * @param {object|null|undefined} product
 * @param {string} [slug]
 */
export function buildProductSeo(product, slug) {
  const name = product?.name?.trim() || 'Produit';
  const description =
    truncateMetaDescription(product?.description) ||
    `Découvrez ${name} et commandez en ligne sur ${SITE.name}.`;
  const firstImage = product?.imageUrls?.[0];
  const image = firstImage ? getImageUrl(firstImage) : SITE.defaultOgImagePath;

  return {
    title: name,
    description,
    image: resolveAbsoluteUrl(image),
    canonicalPath: slug ? `/products/${slug}` : undefined,
    type: 'product',
  };
}

/**
 * SEO page boutique vendeur.
 * @param {object|null|undefined} shop
 * @param {string} [slug]
 */
export function buildShopSeo(shop, slug) {
  const name = shop?.shop_name?.trim() || 'Boutique';
  const location = shop?.location?.trim();
  const description = location
    ? `Boutique ${name} — ${location}. Produits et offres sur ${SITE.name}.`
    : `Découvrez la boutique ${name} sur ${SITE.name}.`;
  const logo = shop?.logo_url ? getImageUrl(shop.logo_url) : SITE.defaultOgImagePath;

  return {
    title: name,
    description: truncateMetaDescription(description),
    image: resolveAbsoluteUrl(logo),
    canonicalPath: slug ? `/boutiques/${slug}` : undefined,
    type: 'website',
  };
}

/**
 * SEO liste catégories (recherche ou filtre optionnel).
 * @param {{ search?: string, categoryName?: string }} [options]
 */
export function buildCategoriesSeo(options = {}) {
  const search = options.search?.trim();
  const categoryName = options.categoryName?.trim();

  if (search) {
    return {
      title: `Recherche : ${search}`,
      description: `Résultats pour « ${search} » sur ${SITE.name}.`,
      canonicalPath: `/categories?search=${encodeURIComponent(search)}`,
      type: 'website',
    };
  }

  if (categoryName) {
    return {
      title: categoryName,
      description: `Produits de la catégorie ${categoryName} sur ${SITE.name}.`,
      canonicalPath: STATIC_PAGES.categories.canonicalPath,
      type: 'website',
    };
  }

  return { ...STATIC_PAGES.categories, type: 'website' };
}
